import { fetchPublicConfig } from "../api/publicConfig";
import { ensureDataLayer } from "./events";

declare global {
  interface Window {
    __quizGtmContainerId?: string;
    __quizMetaPixelId?: string;
    fbq?: MetaPixel;
    _fbq?: MetaPixel;
  }
}

type MetaPixel = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  loaded: boolean;
  version: string;
};

const pendingMetaLeads: Array<{ eventId: string; quizSlug: string }> = [];

export function initializeTracking(): void {
  ensureDataLayer();
  void fetchPublicConfig()
    .then((config) => {
      loadGtm(config.tracking.gtmContainerId);
      loadMetaPixel(config.tracking.metaPixelId);
    })
    .catch(() => undefined);
}

export function loadGtm(containerId: string): void {
  const id = containerId.trim();
  if (!id || window.__quizGtmContainerId === id) return;
  window.__quizGtmContainerId = id;
  ensureDataLayer().push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.append(script);
}

export function loadMetaPixel(pixelId?: string): void {
  const id = pixelId?.trim() ?? "";
  if (!/^\d+$/.test(id) || window.__quizMetaPixelId === id) return;
  window.__quizMetaPixelId = id;
  const fbq = ensureMetaPixel();
  fbq("set", "autoConfig", false, id);
  fbq("init", id);
  fbq("trackSingle", id, "PageView");
  for (const lead of pendingMetaLeads.splice(0)) sendMetaLead(fbq, id, lead.eventId, lead.quizSlug);
}

export function trackMetaLead(eventId: string, quizSlug: string): void {
  if (!eventId) return;
  const id = window.__quizMetaPixelId;
  if (!id || !window.fbq) {
    pendingMetaLeads.push({ eventId, quizSlug });
    return;
  }
  sendMetaLead(window.fbq, id, eventId, quizSlug);
}

function sendMetaLead(fbq: MetaPixel, pixelId: string, eventId: string, quizSlug: string): void {
  try {
    fbq("trackSingle", pixelId, "Lead", { content_name: quizSlug }, { eventID: eventId });
  } catch {
    // Third-party tracking must not interrupt the saved-lead flow.
  }
}

function ensureMetaPixel(): MetaPixel {
  if (window.fbq) return window.fbq;
  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  }) as MetaPixel;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.append(script);
  return fbq;
}
